<template>
	<div v-if="mensagemDeErro || errorSomar"> {{ mensagemDeErro || getErrorMessage(errorSomar) }} </div>
	<AppLoading v-else-if="!usuario || !somar"/>
	<div v-else style="max-width: 680px; margin: auto;">
		<v-carousel
			v-if="somar.banners.length"
			cycle
			height="240px"
			hide-delimiter-background
			hide-delimiters
			show-arrows-on-hover
			prev-icon="mdi-chevron-left"
			next-icon="mdi-chevron-right"
		>
			<v-carousel-item
				v-for="banner in somar.banners"
				:key="banner"
				transition="fade-transition"
			>
				<div>
					<ZoomImage
						:src="banner"
						ref="zoomImage"
					>
						<template #activator="{ on }">
							<v-img
								contain
								height="240px"
								:src="banner"
								v-on="on"
							/>
						</template>
					</ZoomImage>
				</div>
			</v-carousel-item>
		</v-carousel>

		<v-card-title>
			<div class="mx-auto">
				<div class="d-flex align-center">
					<img
						src="img/icons/ms-icon-310x310.png"
						width="32px"
						class="align-self-start mr-2"
					>
					<div>
						Bem vindo Ir&#x2234; {{ usuario.nomeCompleto.split(' ')[0] }}
					</div>
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-icon
								right
								color="primary"
								v-on="on"
								v-if="ehAniversario"
							>mdi-cake-variant</v-icon>
						</template>
						Feliz aniversário 🎂
					</v-tooltip>
				</div>
			</div>
		</v-card-title>

		<div style="max-width: 480px; margin: auto;">
			<v-list subheader>
				<v-subheader>Acessos rápidos</v-subheader>
				<v-list-item>
					<router-link
						v-for="link in links"
						:key="link.nome"
						:to="link.route"
						tag="div"
						class="d-flex flex-column align-center"
						style="width: 72px"
					>
						<v-btn
							outlined
							icon
							color="primary"
							large
						>
							<v-icon> {{ link.icone }} </v-icon>
						</v-btn>
						<div class="primary--text subtitle-2 pt-1"> {{ link.nome }} </div>
					</router-link>
				</v-list-item>
	
				<template v-if="!visitante">
					<v-subheader>
						Novidades
						<v-progress-circular
							indeterminate
							color="primary"
							size="10"
							width="1"
							class="ml-2"
							v-if="isFetching"
						/>
					</v-subheader>
	
					<v-list-item v-if="errorNovidades">
						<v-alert
							type="error"
							color="error"
							outlined
							border="left"
							colored-border
							dismissible
							class="flex-grow-1"
						>
							{{ getErrorMessage(errorNovidades) }}
						</v-alert>
					</v-list-item>
					<template v-else-if="application.novidade">
						<template v-if="application.temNovidades">
							<v-list-item v-if="application.novidade.novosServicos">
								<v-alert
									type="info"
									color="primary"
									outlined
									border="left"
									colored-border
									dismissible
									class="flex-grow-1"
									@input="() => application.visualizar({ ultVisualizacaoDeServicos: dayjs().toISOString() })"
								>
									IIr. cadastraram <router-link :to="{ name: 'busca', query: { ...queryNovosServicos, recentes: queryNovosServicos.recentes.toString() } }"> <strong>{{ application.novidade.novosServicos }}</strong> novo(s) serviço(s)</router-link>.
								</v-alert>
							</v-list-item>
							<v-list-item v-if="application.novidade.novosCandidatos">
								<v-alert
									type="info"
									color="primary"
									outlined
									border="left"
									colored-border
									dismissible
									class="flex-grow-1"
									@input="() => application.visualizar({ ultVisualizacaoDeCandidatos: dayjs().toISOString() })"
								>
									IIr. cadastraram <router-link :to="{ name: 'candidatos' }"> <strong>{{ application.novidade.novosCandidatos }}</strong> novo(s) candidato(s)</router-link>.
								</v-alert>
							</v-list-item>
						</template>
						<v-list-item v-else>
							<v-list-item-content class="font-italic font-weight-light">
								Nenhuma novidade
							</v-list-item-content>
						</v-list-item>
					</template>
				</template>
			</v-list>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { authModule, userModule } from '../store'
import Irmao from '../../common/types/Irmao'
import IrmaoService from '../services/IrmaoService'
import { getErrorMessage } from '@/helpers/error-helpers'
import { obterParametrosDeNovosServicos } from '@/shareds/sharedBusca'
import { useQuery } from '@tanstack/vue-query'
import { application } from '@/store'
import dayjs from 'dayjs'
import SomarService from '@/services/SomarService'
import ZoomImage from '@/components/ZoomImage.vue'
import { éVisitante } from '../../common/shareds/sharedAuth'

const queryNovosServicos = obterParametrosDeNovosServicos()

const { data: usuario, error } = useQuery({
	queryKey: ['eu'],
	queryFn: IrmaoService.me,
	enabled: authModule.isAuth,
})

const { error: errorNovidades, isFetching } = useQuery({
	queryKey: ['novidades'],
	queryFn: IrmaoService.novidades,
	// só depois de saber quem é o usuário: visitante não tem novidades
	enabled: computed(() => authModule.isAuth && !!usuario.value && !éVisitante(usuario.value)),
})

const { data: somar, error: errorSomar } = useQuery({
	queryKey: ['somar', 'Somar Itapocu'],
	queryFn: () => SomarService.get('Somar Itapocu'),
	enabled: authModule.isAuth,
})

watch(usuario, (valor) => {
	userModule.setCurrentUser(valor || null)
})

const mensagemDeErro = computed(() => {
	return error.value ? getErrorMessage(error.value) : ''
})

const irmao = computed(() => 
  usuario.value && usuario.value.role !== 'Visitante'
    ? usuario.value as Irmao
    : null
)

const visitante = computed(() => éVisitante(usuario.value))

const links = computed(() => {
  const perfil = irmao.value
    ? [{
      icone: 'mdi-account',
      nome: 'Perfil',
      route: { name: 'detalhar-irmao', params: { id: irmao.value._id } },
    }]
    : []
  const servicos = {
    icone: 'mdi-handshake',
    nome: 'Serviços',
    route: { name: 'busca', query: { buscarEm: ['servico'] } },
  }

  if (visitante.value || !irmao.value) return [...perfil, servicos]

  return [
    ...perfil,
    {
      icone: 'mdi-bank',
      nome: 'Loja',
      route: { name: 'detalhar-loja', params: { id: irmao.value.loja._id } },
    },
    {
      icone: 'mdi-web',
      nome: 'Potência',
      route: { name: 'detalhar-potencia', params: { id: irmao.value.potencia._id } },
    },
    {
      icone: 'mdi-math-compass',
      nome: 'Somar',
      route: { name: 'detalhar-somar', params: { id: 'Somar Itapocu' } },
    },
    servicos,
  ]
})

const ehAniversario = computed(() => {
	if (!irmao.value?.dataDeNascimento) return false

	const birthdate = dayjs(irmao.value.dataDeNascimento);
	const today = dayjs();

	return birthdate.date() === today.date() && birthdate.month() === today.month();
})
</script>
